import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
  sendEmailVerification,
  User as FirebaseUser,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { sendOTP, verifyOTP } from './otpService';
import { 
  httpsCallable, 
  Functions,
  connectFunctionsEmulator
} from "firebase/functions";
import { auth, db, functions } from "../firebase/config";

// Note: Functions are now configured with the correct region in firebase/config.ts
// For development, you can use the emulator with: connectFunctionsEmulator(functions, "localhost", 5001);

// Initialize Cloud Functions
const sendPasswordChangeOTPFunction = httpsCallable(functions, 'sendPasswordChangeOTP');
const verifyPasswordChangeOTPFunction = httpsCallable(functions, 'verifyPasswordChangeOTP');
const adminChangeUserPasswordFunction = httpsCallable(functions, 'adminChangeUserPassword');
const customPasswordResetFunction = httpsCallable(functions, 'customPasswordReset');

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  displayName: string;
  email: string;
  phoneNumber?: string;
  emailVerified: boolean;
  phoneVerified: boolean;
  isVerified: boolean;
  profilePictureURL?: string;
  storeCredit: number;
  createdAt: Timestamp | null;
  updatedAt: string | null;
  role?: "customer" | "manager" | "staff" | "admin";
  systemRole?: "admin"; // System-level roles for admin functions
  markedForDeletion?: boolean;
  deletionRequestedAt?: Timestamp | null;
}

// Registration data interface
interface RegistrationData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phoneNumber?: string; // Made optional
}

// OTP verification interface
interface VerifyOtpData {
  userId: string;
  otp: string;
  type: "email" | "phone";
}

// Update unverified email/phone interfaces
interface UpdateEmailData {
  userId: string;
  newEmail: string;
}

interface UpdatePhoneData {
  userId: string;
  newPhoneNumber: string;
}

export const registerUser = async (userData: {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phoneNumber?: string;
}): Promise<FirebaseUser> => {
  try {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      userData.email,
      userData.password
    );

    const displayName = `${userData.firstName} ${userData.lastName}`;

    await updateProfile(userCredential.user, {
      displayName: displayName,
    });

    await setDoc(doc(db, "users", userCredential.user.uid), {
      firstName: userData.firstName,
      lastName: userData.lastName,
      displayName: displayName,
      email: userData.email,
      phoneNumber: userData.phoneNumber || "",
      emailVerified: false,
      phoneVerified: false,
      isVerified: false,
      profilePictureURL: "",
      storeCredit: 0,
      createdAt: serverTimestamp(),
      updatedAt: new Date().toISOString(),
    });

    await sendEmailVerification(userCredential.user);

    return userCredential.user;
  } catch (error) {
    console.error("Error registering user:", error);
    throw error;
  }
};

// Send verification email
export const sendVerificationEmail = async (): Promise<void> => {
  const currentUser = auth.currentUser;

  if (!currentUser) {
    throw new Error("No user is currently logged in");
  }

  try {
    await sendEmailVerification(currentUser);
  } catch (error) {
    console.error("Error sending verification email:", error);
    throw error;
  }
};

// Update email verification status in Firestore
export const updateEmailVerificationStatus = async (): Promise<void> => {
  const currentUser = auth.currentUser;

  if (!currentUser) {
    throw new Error("No user is currently logged in");
  }

  // Force refresh to get the latest token data
  await currentUser.reload();

  try {
    if (currentUser.emailVerified) {
      const userRef = doc(db, "users", currentUser.uid);
      await updateDoc(userRef, {
        emailVerified: true,
        isVerified: true,
        updatedAt: new Date().toISOString(),
      });
    }
  } catch (error) {
    console.error("Error updating email verification status:", error);
    throw error;
  }
};

export const signInWithEmail = async (
  email: string,
  password: string
): Promise<FirebaseUser> => {
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    return userCredential.user;
  } catch (error) {
    console.error("Error signing in:", error);
    throw error;
  }
};

export const resetPassword = async (email: string): Promise<void> => {
  try {
    // Use custom password reset function instead of Firebase default
    const result = await customPasswordResetFunction({ email });
    console.log("Password reset email sent:", result.data);
  } catch (error) {
    console.error("Error sending password reset:", error);
    throw error;
  }
};

// Get current user data from Firestore
export const getCurrentUser = async (): Promise<User | null> => {
  const currentUser = auth.currentUser;

  if (!currentUser) {
    // Check if we have cached user data in localStorage that we can use temporarily
    const cachedUserData = localStorage.getItem('userData');
    if (cachedUserData) {
      try {
        const userData = JSON.parse(cachedUserData);
        // Note: Without a current Firebase user, we can't validate the cached data
        // This should only be used as a temporary fallback during auth initialization
        if (userData && userData.id) {
          const hasLoggedCacheMessage = sessionStorage.getItem('hasLoggedCacheMessage');
          if (!hasLoggedCacheMessage) {
            console.log("Using cached user data while auth state initializes");
            sessionStorage.setItem('hasLoggedCacheMessage', 'true');
          }
          return userData as User;
        }
      } catch (error) {
        console.error('Error parsing cached user data:', error);
        localStorage.removeItem('userData');
      }
    }
    return null;
  }

  // Check if we have cached data for the current user
  const cachedUserData = localStorage.getItem('userData');
  if (cachedUserData) {
    try {
      const userData = JSON.parse(cachedUserData);
      // Only use cached data if it belongs to the current user
      if (userData && userData.id !== currentUser.uid) {
        console.log('Clearing stale cached data for different user in getCurrentUser');
        localStorage.removeItem('userData');
      }
    } catch (error) {
      console.error('Error parsing cached user data:', error);
      localStorage.removeItem('userData');
    }
  }

  // Check if we're currently impersonating a user
  const impersonationData = localStorage.getItem('impersonationData');
  if (impersonationData) {
    try {
      const { impersonatedUser } = JSON.parse(impersonationData);
      return impersonatedUser;
    } catch (error) {
      console.error('Error parsing impersonation data:', error);
      localStorage.removeItem('impersonationData');
    }
  }

  try {
    // Force refresh user to get the latest data
    await currentUser.reload();
    
    const userDoc = await getDoc(doc(db, "users", currentUser.uid));
    console.log('getCurrentUser - userDoc exists:', userDoc.exists());
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      console.log('getCurrentUser - userData from Firestore:', userData);
      
      if (currentUser.emailVerified && !userData.emailVerified) {
        await updateEmailVerificationStatus();
        const updatedUserDoc = await getDoc(doc(db, "users", currentUser.uid));
        
        const userDataToReturn = {
          id: currentUser.uid,
          ...updatedUserDoc.data(),
        } as User;
        
        console.log('getCurrentUser - returning updated user data:', userDataToReturn);
        
        // Cache the user data in localStorage for reliability
        localStorage.setItem('userData', JSON.stringify(userDataToReturn));
        
        return userDataToReturn;
      }
      
      const userDataToReturn = {
        id: currentUser.uid,
        ...userData,
      } as User;
      
      console.log('getCurrentUser - returning user data:', userDataToReturn);
      
      // Cache the user data in localStorage for reliability
      localStorage.setItem('userData', JSON.stringify(userDataToReturn));
      
      return userDataToReturn;
    }

    console.log('getCurrentUser - no user document found');
    // No user document found
    return null;
  } catch (error) {
    console.error("Error fetching user data:", error);
    throw error;
  }
};

export const updateUserProfile = async ({
  userId,
  firstName,
  lastName,
  profilePictureURL,
}: {
  userId: string;
  firstName: string;
  lastName: string;
  profilePictureURL?: string;
}): Promise<void> => {
  try {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      throw new Error("No user is currently logged in");
    }

    const displayName = `${firstName} ${lastName}`;

    // Update Firebase Auth profile
    await updateProfile(currentUser, {
      displayName,
      photoURL: profilePictureURL,
    });

    // Update Firestore user document
    const userRef = doc(db, "users", userId);
    await updateDoc(userRef, {
      firstName,
      lastName,
      displayName,
      profilePictureURL: profilePictureURL || "",
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error("Error updating user profile:", error);
    throw error;
  }
};

// Update user password
export const updateUserPassword = async (
  currentPassword: string,
  newPassword: string
): Promise<void> => {
  try {
    const user = auth.currentUser;

    if (!user || !user.email) {
      throw new Error("No user is currently logged in");
    }

    const credential = EmailAuthProvider.credential(
      user.email,
      currentPassword
    );
    await reauthenticateWithCredential(user, credential);

    await updatePassword(user, newPassword);
  } catch (error) {
    console.error("Error updating password:", error);
    throw error;
  }
};
export const updatePasswordWithOTP = async (
  userId: string,
  otp: string,
  newPassword: string
): Promise<void> => {
  try {
    // Ensure user is authenticated
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("You must be logged in to change your password");
    }

    // Verify the authenticated user matches the userId
    if (currentUser.uid !== userId) {
      throw new Error("Unauthorized: Cannot change password for different user");
    }

    // Force refresh the auth token to ensure it's valid
    await currentUser.getIdToken(true);
    
    console.log("Making password change request for user:", currentUser.uid);
    const result = await verifyPasswordChangeOTPFunction({ otp, newPassword });
    
    if (!(result.data as { success: boolean }).success) {
      throw new Error("Failed to update password");
    }
    
  } catch (error) {
    console.error("Error updating password with OTP:", error);
    if (error instanceof Error) {
      throw new Error(error.message);
    }
    throw new Error("Failed to update password. Please try again.");
  }
};

// Admin function to change user password
export const adminChangeUserPassword = async (userId: string, newPassword: string): Promise<void> => {
  try {
    const result = await adminChangeUserPasswordFunction({ userId, newPassword });
    
    if (!(result.data as { success: boolean }).success) {
      throw new Error("Failed to change user password");
    }
    
  } catch (error) {
    console.error("Error changing user password:", error);
    if (error instanceof Error) {
      throw new Error(error.message);
    }
    throw new Error("Failed to change user password. Please try again.");
  }
};

export interface OTPResult {
  sms: boolean;
  email: boolean;
  phone: string | null;
  emailMasked: string | null;
}

export const requestPasswordChangeOTP = async (): Promise<OTPResult> => {
  try {
    // Ensure user is authenticated
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("You must be logged in to request a password change OTP");
    }

    // Force refresh the auth token to ensure it's valid
    console.log("Refreshing auth token for user:", currentUser.uid);
    const freshToken = await currentUser.getIdToken(true);
    console.log("Fresh token obtained, length:", freshToken.length);
    
    // Wait a moment to ensure token is properly set
    await new Promise(resolve => setTimeout(resolve, 100));
    
    console.log("Making password change OTP request for user:", currentUser.uid);
    const result = await sendPasswordChangeOTPFunction();
    console.log("Password change OTP request successful:", result);
    return result.data as OTPResult;
  } catch (error) {
    console.error("Error requesting password change OTP:", error);
    
    // Handle specific Firebase function errors
    if (error && typeof error === 'object' && 'code' in error) {
      const funcError = error as { code: string; message: string };
      if (funcError.code === 'unauthenticated') {
        throw new Error("Authentication failed. Please log out and log back in.");
      }
      throw new Error(funcError.message || "Failed to send verification code. Please try again.");
    }
    
    if (error instanceof Error) {
      throw new Error(error.message);
    }
    throw new Error("Failed to send verification code. Please try again.");
  }
};
export const registerUserAndInitiateOtp = async (data: RegistrationData) => {
  try {
    // Old implementation using httpsCallable
    // const registerFunction = httpsCallable(functions, "registerUserAndInitiateOtp");
    // const result = await registerFunction(data);
    // return result.data as {...};
    
    // New implementation using fetch for HTTP functions
    const response = await fetch(
      "https://us-central1-storecreditapp.cloudfunctions.net/registerUserAndInitiateOtp",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Registration failed");
    }
    
    return await response.json();
  } catch (error: any) {
    console.error("Registration error:", error);
    throw error;
  }
};

export const verifyOtp = async (data: VerifyOtpData) => {
  try {
    const response = await fetch(
      "https://us-central1-storecreditapp.cloudfunctions.net/verifyOtp",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...data, returnToken: true }), // Request a custom token when verifying
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "OTP verification failed");
    }
    
    const result = await response.json();
    
    // Make sure we're returning any custom token that was included in the response
    // The backend may return token, customToken, or both depending on the implementation
    return {
      ...result,
      customToken: result.customToken || result.token || null
    };
  } catch (error: any) {
    console.error("OTP verification error:", error);
    throw error;
  }
};

export const resendOtp = async (data: {
  userId: string;
  type: "email" | "phone";
  email?: string;          // Optional email address to send to
  resendSame?: boolean;    // Flag to request resending the same OTP
}) => {
  try {
    const response = await fetch(
      "https://us-central1-storecreditapp.cloudfunctions.net/resendOtp",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to resend OTP");
    }
    
    return await response.json();
  } catch (error: any) {
    console.error("Resend OTP error:", error);
    throw error;
  }
};

/**
 * Update unverified email and send new OTP
 */
export const updateUnverifiedEmail = async (data: UpdateEmailData) => {
  try {
    const response = await fetch(
      "https://us-central1-storecreditapp.cloudfunctions.net/updateUnverifiedEmail",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to update email");
    }
    
    return await response.json();
  } catch (error: any) {
    console.error("Update email error:", error);
    throw error;
  }
};

/**
 * Update unverified phone number and send new OTP
 */
export const updateUnverifiedPhoneNumber = async (data: UpdatePhoneData) => {
  try {
    const response = await fetch(
      "https://us-central1-storecreditapp.cloudfunctions.net/updateUnverifiedPhoneNumber",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to update phone number");
    }
    
    return await response.json();
  } catch (error: any) {
    console.error("Update phone error:", error);
    throw error;
  }
};

export const signIn = async (email: string, password: string) => {
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    return userCredential.user;
  } catch (error: any) {
    console.error("Sign in error:", error);
    throw error;
  }
};

/**
 * Mark user for deletion
 */
export const markUserForDeletion = async (): Promise<void> => {
  const currentUser = auth.currentUser;

  if (!currentUser) {
    throw new Error("No user is currently logged in");
  }

  try {
    const userRef = doc(db, "users", currentUser.uid);
    await updateDoc(userRef, {
      markedForDeletion: true,
      deletionRequestedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error marking user for deletion:", error);
    throw error;
  }
};

/**
 * Cancel deletion request for the user
 */
export const cancelDeletionRequest = async (): Promise<void> => {
  const currentUser = auth.currentUser;

  if (!currentUser) {
    throw new Error("No user is currently logged in");
  }

  try {
    const userRef = doc(db, "users", currentUser.uid);
    await updateDoc(userRef, {
      markedForDeletion: false,
      deletionRequestedAt: null,
    });
  } catch (error) {
    console.error("Error canceling deletion request:", error);
    throw error;
  }
};

// Impersonation functions for admin users
export const startImpersonation = async (targetUserId: string): Promise<User> => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('No authenticated user');
  }

  // Check if current user is an admin
  const adminUser = await getCurrentUser();
  if (!adminUser || adminUser.systemRole !== 'admin') {
    throw new Error('Only admins can impersonate users');
  }

  // Get the target user data
  const targetUserDoc = await getDoc(doc(db, "users", targetUserId));
  if (!targetUserDoc.exists()) {
    throw new Error('Target user not found');
  }

  const targetUserData = targetUserDoc.data();
  const targetUser: User = {
    id: targetUserId,
    firstName: targetUserData.firstName || '',
    lastName: targetUserData.lastName || '',
    displayName: targetUserData.displayName || '',
    email: targetUserData.email || '',
    phoneNumber: targetUserData.phoneNumber || '',
    emailVerified: targetUserData.emailVerified || false,
    phoneVerified: targetUserData.phoneVerified || false,
    isVerified: targetUserData.emailVerified || false,
    profilePictureURL: targetUserData.profilePictureURL || '',
    storeCredit: targetUserData.storeCredit || 0,
    createdAt: targetUserData.createdAt || null,
    updatedAt: targetUserData.updatedAt || null,
    role: targetUserData.role || 'customer',
    systemRole: targetUserData.systemRole,
    markedForDeletion: targetUserData.markedForDeletion || false,
    deletionRequestedAt: targetUserData.deletionRequestedAt || null,
  };

  // Store impersonation data in localStorage
  const impersonationData = {
    originalAdminId: currentUser.uid,
    originalAdminData: adminUser,
    impersonatedUser: targetUser,
    startTime: new Date().toISOString()
  };
  
  localStorage.setItem('impersonationData', JSON.stringify(impersonationData));
  localStorage.setItem('userData', JSON.stringify(targetUser));

  return targetUser;
};

export const endImpersonation = async (): Promise<User> => {
  const impersonationData = localStorage.getItem('impersonationData');
  if (!impersonationData) {
    throw new Error('No active impersonation session');
  }

  try {
    const { originalAdminData } = JSON.parse(impersonationData);
    
    // Clear impersonation data
    localStorage.removeItem('impersonationData');
    localStorage.setItem('userData', JSON.stringify(originalAdminData));

    return originalAdminData;
  } catch (error) {
    console.error('Error ending impersonation:', error);
    throw new Error('Failed to end impersonation');
  }
};

export const getImpersonationStatus = (): {
  isImpersonating: boolean;
  originalAdmin?: User;
  impersonatedUser?: User;
} => {
  const impersonationData = localStorage.getItem('impersonationData');
  if (!impersonationData) {
    return { isImpersonating: false };
  }

  try {
    const data = JSON.parse(impersonationData);
    return {
      isImpersonating: true,
      originalAdmin: data.originalAdminData,
      impersonatedUser: data.impersonatedUser
    };
  } catch (error) {
    console.error('Error parsing impersonation data:', error);
    return { isImpersonating: false };
  }
};

export const isCurrentlyImpersonating = (): boolean => {
  return !!localStorage.getItem('impersonationData');
};

/**
 * Verify phone using a link token sent via SMS
 */
export const verifyPhoneLinkToken = async (data: {
  token: string;
}) => {
  try {
    // Add validation to ensure the token is not missing or malformed
    if (!data.token) {
      throw new Error('Token is missing or invalid');
    }
    
    // Log token data being sent (safely)
    console.log('Sending token verification request. Token length:', 
                data.token.length, 
                'First 10 chars:', 
                data.token.substring(0, 10) + '...');
    
    const response = await fetch(
      "https://us-central1-storecreditapp.cloudfunctions.net/verifyPhoneLink",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to verify phone number');
    }

    // If successful, return the response data
    return await response.json();
  } catch (error: any) {
    console.error("Error verifying phone link:", error);
    throw error;
  }
};
